import { Injectable } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { IModalService } from 'shared-components';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { Logger } from 'app/core/logger/services/logger.service';
import { ModalModel } from 'app/core/modals/services/modal.model';
import { WatchVideoComponent } from 'app/shared/modals/watch-video/watch-video.component';
import { IConfirmDialogData } from '../confirm-dialog/confirm-dialog-data';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { IInputDialogData } from '../input-dialog/input-dialog-data';
import { InputDialogComponent } from '../input-dialog/input-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class ModalService implements IModalService {

  private static readonly BUTTON_KEYS = {
    YES: 'MODAL.BUTTONS_YES',
    NO: 'MODAL.BUTTONS_NO',
    LEAVE: 'MODAL.BUTTONS_LEAVE',
    CANCEL: 'MODAL.BUTTONS_CANCEL',
    SUBMIT: 'MODAL.BUTTONS_SUBMIT'
  } as const;

  public constructor(
    private readonly modalService: BsModalService,
    private readonly logger: Logger,
    private readonly translationService: TranslationService,
  ) { }

  public displayModal<TData>(
    component: any,
    data: Partial<TData> = {},
    size = 'md',
    allowEscape: boolean | 'static' = 'static',
    backdrop: boolean | 'static' = false,
    centered = false
  ): BsModalRef<TData> {
    const centerClass = centered ? ' modal-dialog-centered' : '';

    return this.modalService.show(component, {
      backdrop: allowEscape || backdrop,
      class: `modal-${size} ${centerClass}`,
      ignoreBackdropClick: !allowEscape,
      initialState: data,
      keyboard: allowEscape === 'static' ? false : allowEscape
    });
  }

  public displayCustomModal<TData>(component: any, options?: ModalModel<TData>): BsModalRef<TData> {
    const size = options?.size ? `modal-${options.size}` : 'modal-md';

    return this.modalService.show(component, {
      backdrop: options?.backdrop ?? true,
      class: `${size} ${options?.className ?? ''}`,
      ignoreBackdropClick: options?.ignoreBackdropClick ?? false,
      initialState: options?.data,
      keyboard: options?.keyboard ?? true,
      animated: options?.animated ?? true
    });
  }

  public displayTwoButtonConfirm(
    title: string,
    message: string,
    firstButtonText: string,
    secondButtonText: string,
    firstButtonHandler: () => void
  ): void {
    this.displayModal<IConfirmDialogData>(ConfirmDialogComponent, {
      body: this.translationService.instant(message),
      firstButtonHandler,
      firstButtonText,
      secondButtonText,
      title
    } as IConfirmDialogData, 'sm', true);
  }

  public displayYesNoConfirm(title: string, message: string, yesCallback: () => void): void {
    this.displayTwoButtonConfirm(
      title,
      message,
      ModalService.BUTTON_KEYS.YES,
      ModalService.BUTTON_KEYS.NO,
      yesCallback
    );
  }

  public displayLeaveCancel(
    title: string,
    message: string,
    leaveCallback: () => void,
    cancelCallback: () => void
  ): void {
    this.displayModal<IConfirmDialogData>(ConfirmDialogComponent, {
      body: this.translationService.instant(message),
      firstButtonHandler: leaveCallback,
      firstButtonText: ModalService.BUTTON_KEYS.LEAVE,
      secondButtonHandler: cancelCallback,
      secondButtonText: ModalService.BUTTON_KEYS.CANCEL,
      title
    } as IConfirmDialogData, 'sm', true);
  }

  public displayInputModal(title: string, message: string, yesCallback: (value: string) => void): void {
    this.displayModal<IInputDialogData>(InputDialogComponent, {
      body: this.translationService.instant(message),
      firstButtonHandler: yesCallback,
      firstButtonText: ModalService.BUTTON_KEYS.SUBMIT,
      secondButtonText: ModalService.BUTTON_KEYS.CANCEL,
      title
    } as IInputDialogData, 'sm', true);
  }

  public showVideo(videoId: string, titile = '', description = ''): void {
    this.displayModal<WatchVideoComponent>(WatchVideoComponent, {
      description,
      title: titile,
      videoId
    }, 'lg', true);
  }

  public showInputBlocker(durationInMS?: number, context?: string): () => void {
    const modalRef = this.modalService.show('', {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false
    });

    if (durationInMS && durationInMS > 0) {
      const timeoutId = setTimeout(() => {
        modalRef.hide();

        this.logger.error(
          `[InputBlocker] Timed out after ${durationInMS}ms while waiting for: ${context || 'unknown context'}.`
        );
      }, durationInMS);

      return () => {
        modalRef.hide();
        clearTimeout(timeoutId);
      };
    }

    return () => modalRef.hide();
  }

  public closeAllModals(): void {
    // Force cleanup all modal DOM elements
    const modalBackdrops = document.querySelectorAll('bs-modal-backdrop');
    const modals = document.querySelectorAll('modal-container');

    modalBackdrops.forEach(backdrop => {
      backdrop.remove();
    });
    modals.forEach(modal => {
      modal.remove();
    });

    // Clean up body classes and styles
    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('padding-right');
    document.body.style.removeProperty('overflow');
  }
}
